import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

type FileUploadRow = Tables<'file_uploads'>;

interface UploadHistoryProps {
  organizationId: string;
  mandateId: string;
  refreshKey?: number;
}

interface HistoryItem {
  id: string;
  fileName: string;
  timestamp: string;
  status: FileUploadRow['processing_status'];
  error?: string | null;
}

const iconForStatus = (status: FileUploadRow['processing_status']) => {
  switch (status) {
    case 'completed':
      return { icon: 'fa-check', iconBg: 'bg-green-100', iconColor: 'text-green-600' };
    case 'error':
      return { icon: 'fa-triangle-exclamation', iconBg: 'bg-brand-danger-bg', iconColor: 'text-brand-danger' };
    default:
      return { icon: 'fa-hourglass-half', iconBg: 'bg-yellow-100', iconColor: 'text-yellow-600' };
  }
};

export const UploadHistory: React.FC<UploadHistoryProps> = ({ organizationId, mandateId, refreshKey = 0 }) => {
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);

  useEffect(() => {
    if (!organizationId || !mandateId) {
      setHistoryItems([]);
      return;
    }

    const loadHistory = async () => {
      const { data, error } = await supabase
        .from('file_uploads')
        .select('id, file_name, processing_status, processed_at, created_at, error_message')
        .eq('organization_id', organizationId)
        .eq('mandate_id', mandateId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Failed to load upload history', error);
        setHistoryItems([]);
        return;
      }

      const mapped = (data ?? []).map<HistoryItem>((row) => ({
        id: row.id,
        fileName: row.file_name,
        timestamp: row.processed_at || row.created_at,
        status: row.processing_status,
        error: row.error_message,
      }));

      setHistoryItems(mapped);
    };

    void loadHistory();
  }, [organizationId, mandateId, refreshKey]);

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-brand-primary">Upload History</h3>
      </div>
      <div className="space-y-1 -mx-2">
        {historyItems.length === 0 ? (
          <p className="px-2 py-3 text-sm text-brand-text-secondary">No uploads yet. Upload a Swiss bank export to get started.</p>
        ) : (
          historyItems.map((item) => {
            const icon = iconForStatus(item.status);
            const isError = item.status === 'error';

            return (
              <div
                key={item.id}
                className={`flex items-center gap-4 px-2 py-2 rounded-lg hover:bg-brand-secondary ${isError ? 'bg-brand-danger-bg/40' : ''}`}
              >
                <div className={`w-8 h-8 flex items-center justify-center ${icon.iconBg} rounded-lg`}>
                  <i className={`${icon.icon} ${icon.iconColor}`}></i>
                </div>
                <div className="flex-grow">
                  <p className="text-sm font-medium text-brand-text-primary">{item.fileName}</p>
                  <p className={`text-xs ${isError ? 'text-brand-danger' : 'text-brand-text-secondary'}`}>
                    {new Date(item.timestamp).toLocaleString()} {item.error ? `• ${item.error}` : ''}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
